import React from "react";
import { useParams } from "react-router-dom";
import { Button, Box } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";

import {
  ComplianceWithdrawalStatusSelect,
  WithdrawalPaymentStatus,
} from "../../../../../components/ui";
import { getTimeByAmericaChicagoTz } from "../../../../../utils/moment";
import { IntlFormatUSD } from "../../../../../utils";
import {
  getAmountToBePay,
  getNewAccountBalance,
} from "../../WithdrawalRequestFormWithRequestsList/utils";
import {
  IWithdrawalWithAccountInfo,
  TCompletedStatuses,
} from "../../../../../api/funds/index.types";

import { StyledTrWithHover } from "../../../../../useGeneralStyles";
import { DialogConfirm } from "../../../../Customer/components";
import { useAdminProviderDataContext } from "../../../../../context";
import { CENTS_IN_DOLLAR } from "../../../../../constants";

const noWrapStyle = { whiteSpace: "nowrap" } as const;
const actionsContainerSx = {
  display: "flex",
  flexDirection: "column" as const,
  justifyContent: "center",
  alignItems: "center",
  "& p": {
    margin: "0 0 5px 0",
    textTransform: "capitalize",
  },
};
const buttonsContainerSx = { display: "flex" } as const;

interface IProps {
  withdrawals: IWithdrawalWithAccountInfo[];
  handleChangeWithdrawalStatus: (
    e: React.MouseEvent,
    id: number,
    status: TCompletedStatuses
  ) => void;
  handleEditWithdrawal: (withdrawal: IWithdrawalWithAccountInfo) => void;
}
const EDITABLE_STATUSES = ["pending", "on hold"];
const TableBody: React.FC<IProps> = ({
  withdrawals,
  handleChangeWithdrawalStatus,
  handleEditWithdrawal,
}) => {
  const { id }: { id: string } = useParams();
  const isShowCustomerInfoColumn = !id;

  const {
    user: { allowed_features },
  } = useAdminProviderDataContext();

  return (
    <tbody>
      {withdrawals.map((withdrawal) => {
        const isEditable = EDITABLE_STATUSES.includes(withdrawal.status);
        const {
          manual_withdrawal_amount,
          changed_status_time,
          status,
          comment,
          manual_deduction,
          watermark_level,
          account_balance,
          amount_requested,
          initialization_date,
          id,
          compliance_status,
        } = withdrawal;
        const {
          amountToPay,
          fee: { regular_deduction_rate, conveniences_fees, sum },
        } = getAmountToBePay(withdrawal);
        return (
          <StyledTrWithHover
            key={id}
            sx={{
              ...(isEditable && { "& td": { backgroundColor: "#ffff003b !important" } }),
            }}
          >
            <td onClick={() => isEditable && handleEditWithdrawal(withdrawal)}>
              {isEditable && <EditIcon />}
            </td>
            <td>
              <Box component="span" sx={noWrapStyle}>
                {getTimeByAmericaChicagoTz(initialization_date, true)}
              </Box>{" "}
              CT
            </td>
            {isShowCustomerInfoColumn && (
              <>
                <td>{withdrawal?.account?.name}</td>
                <td>{withdrawal?.account?.email}</td>
              </>
            )}

            <td>{withdrawal.traded_account?.e2t_account_id || withdrawal.account_name}</td>
            <td>{withdrawal.traded_account?.prop_firm}</td>
            <td>{IntlFormatUSD(amount_requested / CENTS_IN_DOLLAR)}</td>
            <td>{IntlFormatUSD(account_balance / CENTS_IN_DOLLAR)}</td>
            <td>{IntlFormatUSD(watermark_level)}</td>
            <td>{IntlFormatUSD(getNewAccountBalance(withdrawal) / CENTS_IN_DOLLAR)}</td>
            <td>{manual_deduction / CENTS_IN_DOLLAR}</td>
            <td>
              <Box sx={noWrapStyle}>
                <p style={{ margin: 0 }}>
                  Regular deduct {IntlFormatUSD(regular_deduction_rate / CENTS_IN_DOLLAR)}
                </p>
                <p style={{ margin: 0 }}>Convenience Fee: {IntlFormatUSD(conveniences_fees)}</p>
                <p style={{ margin: 0 }}>Sum: {IntlFormatUSD(sum / CENTS_IN_DOLLAR)}</p>
              </Box>
            </td>
            <td>{comment}</td>
            <td>{IntlFormatUSD((manual_withdrawal_amount || amountToPay) / CENTS_IN_DOLLAR)}</td>
            <td>{changed_status_time && getTimeByAmericaChicagoTz(changed_status_time, true)}</td>
            <td>
              <ComplianceWithdrawalStatusSelect
                compliance_status={compliance_status}
                id={withdrawal.id}
                payment_status={status}
              />
            </td>
            <td>
              <Box sx={actionsContainerSx}>
                <p style={{ margin: 0 }}>
                  {status !== "pending" && <WithdrawalPaymentStatus status={status} />}
                </p>
                {status === "pending" && (
                  <Box component="span" sx={buttonsContainerSx}>
                    {allowed_features.access_withdrawals && (
                      <Button
                        size="small"
                        onClick={(e) => handleChangeWithdrawalStatus(e, withdrawal.id, "paid")}
                      >
                        Paid
                      </Button>
                    )}

                    <DialogConfirm
                      isShowMainButton
                      dialogTitle="Delete the withdrawal"
                      contentText="This will delete the withdrawal request. Are you sure?"
                      buttonText="Decline"
                      onClickConfirm={(e: React.MouseEvent) => {
                        handleChangeWithdrawalStatus(e, withdrawal.id, "canceled");
                      }}
                      externalStyles={{
                        backgroundColor: "#e53935",
                        minWidth: 75,
                        textTransform: "uppercase",
                      }}
                    />
                  </Box>
                )}
              </Box>
            </td>
          </StyledTrWithHover>
        );
      })}
    </tbody>
  );
};

export default TableBody;
